import d3 from 'd3';
import { Measure } from '../common/measure';

require('./pie.component.scss');

/*@ngInject*/
class PieController {
  constructor($scope, $rootScope, $element, RouterService, rx) {
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$element = $element;
    this.router = RouterService;

    let subscriptions = [
      rx.Observable.merge(
        rx.Observable.merge(
          $scope.$eventToObservable('resize'),
          $scope.$eventToObservable('splitterChanged'),
        ).debounce(100),
        $scope.$eventToObservable('beforePrint'),
        $scope.$eventToObservable('afterPrint'),
        $scope.$eventToObservable('init'),
      )
        .subscribe(() => {
          this.render();
        }),
    ];
    $scope.$on('$destroy', () => {
      subscriptions.forEach((subscription) => {
        subscription.dispose();
      });
    });
  }

  $postLink() {
  }

  get aggregate() {
    return this._aggregate;
  }

  set aggregate(value) {
    this._aggregate = value;
    this.render();
  }

  render() {
    // console.log(`pie render ${this.$element.width()}x${this.$element.height()}`);
    let _this = this;

    let statChart = this.$element[0].querySelector('.stat-chart');
    let statSequence = this.$element[0].querySelector('.stat-sequence');
    let statLegend = this.$element[0].querySelector('.stat-legend');

    let aggregate = this.aggregate;
    if (!aggregate || !aggregate.cells) {
      return;
    }
    this.$element.find('svg').remove();

    let width = this.$element.width();

    // Dimensions of sunburst.
    let height = Math.min(
      this.$element.width(),
      (this.$element.height() - 100),
    );
    let radius = Math.min(width, height) / 2;
    if (height < 50 || width < 50) return;

    // Breadcrumb dimensions: width, height, spacing, width of tip/tail.
    let b = {
      w: 175, h: 30, s: 3, t: 10,
    };

    // Mapping of step names to colors.
    let colors = {};

    let dims = {};

    // Total size of all segments; we set this later, after loading the data.
    let totalSize = 0;

    let vis = d3.select(statChart).append('svg:svg')
      .attr('width', radius * 2)
      .attr('height', radius * 2)
      .append('svg:g')
      .attr('id', 'stat-container')
      .attr(
        'transform',
        'translate(' + radius + ',' + radius + ')',
      )
    ;

    let partition = d3.layout.partition()
      .size([2 * Math.PI, radius * radius])
      .value(d => d.size);

    let arc = d3.svg.arc()
      .startAngle(d => d.x)
      .endAngle(d => d.x + d.dx)
      .innerRadius(d => Math.sqrt(d.y - d.dy))
      .outerRadius(d => Math.sqrt(d.y));

    // Main function to draw and set up the visualization, once we have the data.
    function createVisualization(json) {

      // Basic setup of page elements.
      initializeBreadcrumbTrail();
      drawLegend();

      // Bounding circle underneath the sunburst, to make it easier to detect
      // when the mouse leaves the parent g.
      vis.append('svg:circle')
        .attr('r', radius)
        .style('opacity', 0);

      // For efficiency, filter nodes to keep only those large enough to see.
      let nodes = partition.nodes(json)
        .filter(d => {
          return (d.dx > 0.005); // 0.005 radians = 0.29 degrees
        });

      let path = vis.data([json]).selectAll('path')
        .data(nodes)
        .enter().append('svg:path')
        .attr('display', d => d.depth ? null : 'none')
        .attr('d', arc)
        .attr('fill-rule', 'evenodd')
        .style('fill', d => colors[d.name])
        .style('opacity', 1)
        .style('cursor', 'pointer')
        .on('mouseover', mouseover)
        .on('click', mouseclick);

      // Add the mouseleave handler to the bounding circle.
      vis.on('mouseleave', mouseleave);

      // Get total size of the tree = value of root node from partition.
      totalSize = path.node() ? path.node().__data__.value : 0;
    }

    function mouseclick(d) {
      _this.router.drilldownFilter(d.filter);
      _this.$rootScope.$digest();
    }

    // Fade all but the current sequence, and show it in the breadcrumb trail.
    function mouseover(d) {
      let sequenceArray = getAncestors(d);
      let cell = {};
      cell[_this.aggregate.filter.measure] = d.value;
      let val = Measure.getMeasure(cell, _this.aggregate.filter.measure);
      updateBreadcrumbs(sequenceArray, val);

      // Fade all the segments.
      d3.selectAll('path')
        .style('opacity', 0.3);

      // Then highlight only those that are an ancestor of the current segment.
      vis.selectAll('path')
        .filter(node => (sequenceArray.indexOf(node) >= 0))
        .style('opacity', 1);
    }

    // Restore everything to full opacity when moving off the visualization.
    function mouseleave() {

      // Hide the breadcrumb trail
      d3.select(statSequence).select('#stat-trail')
        .style('visibility', 'hidden');

      // Deactivate all segments during transition.
//               d3.selectAll("path").on("mouseover", null);

      // Transition each segment to full opacity and then reactivate it.
      d3.select(statChart).selectAll('path')
        .transition()
        .duration(1000)
        .style('opacity', 1)
        .each('end', function () {
          d3.select(this).on('mouseover', mouseover);
        });
    }

    // Given a node in a partition layout, return an array of all of its ancestor
    // nodes, highest first, but excluding the root.
    function getAncestors(node) {
      let path = [];
      let current = node;
      while (current.parent) {
        path.unshift(current);
        current = current.parent;
      }
      return path;
    }

    function initializeBreadcrumbTrail() {
      // Add the svg area.
      let trail = d3.select(statSequence).append('svg:svg')
        .attr('width', width)
        .attr('height', 50)
        .attr('id', 'stat-trail');
      // Add the label at the end, for the percentage.
      trail.append('svg:text')
        .attr('id', 'stat-endlabel')
        .style('fill', '#000');
    }

    // Generate a string that describes the points of a breadcrumb polygon.
    function breadcrumbPoints(d, i) {
      let points = [];
      points.push('0,0');
      points.push(b.w + ',0');
      points.push(b.w + b.t + ',' + (b.h / 2));
      points.push(b.w + ',' + b.h);
      points.push('0,' + b.h);
      if (i > 0) { // Leftmost breadcrumb; don't include 6th vertex.
        points.push(b.t + ',' + (b.h / 2));
      }
      return points.join(' ');
    }

    // Update the breadcrumb trail to show the current sequence and percentage.
    function updateBreadcrumbs(nodeArray, total) {

      // Data join; key function combines name and depth (= position in sequence).
      let g = d3.select(statSequence).select('#stat-trail')
        .selectAll('g')
        .data(nodeArray, d => d.name + d.depth);

      // Add breadcrumb and label for entering nodes.
      let entering = g.enter().append('svg:g');

      entering.append('svg:polygon')
        .attr('points', breadcrumbPoints)
        .style('fill', d => colors[d.name]);

      entering.append('svg:text')
        .attr('x', (b.w + b.t) / 2)
        .attr('y', b.h / 2)
        .attr('width', b.w)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .text(d => d.name.substring(0, 25));

      // Set position for entering and updating nodes.
      g.attr('transform', (d, i) => 'translate(' + i * (b.w + b.s) + ', 0)');

      // Remove exiting nodes.
      g.exit().remove();

      // Now move and update the percentage at the end.
      d3.select(statSequence).select('#stat-trail').select('#stat-endlabel')
        .attr('x', (nodeArray.length + 0.5) * (b.w + b.s))
        .attr('y', b.h / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .text(total);

      // Make the breadcrumb trail visible, if it's hidden.
      d3.select(statSequence).select('#stat-trail')
        .style('visibility', '');

    }

    function drawLegend() {

      // Dimensions of legend item: width, height, spacing, radius of rounded rect.
      let li = {
        w: width, h: 30, s: 3, r: 15,
      };

      let dimIndex = 0;//(d3.keys(dims[0]).length == 1) ? 1 : 0;
      let entries = d3.entries(dims[dimIndex]).map(entry => {
        entry.val = colors[entry.key];
        return entry;
      });
      const heightLegend = angular.element(statLegend).height() - 10;
      let maxFits = Math.min(
        entries.length,
        Math.floor((heightLegend) / (li.h + li.s)),
      );
      entries = entries.slice(0, maxFits);

      let mlen = entries.length * (li.h + li.s);
      if (mlen > heightLegend) mlen = heightLegend;

      let legend = d3.select(statLegend).append('svg:svg')
        .attr('width', li.w)
        .attr('height', mlen);

      let g = legend.selectAll('g')
        .data(entries)
        .enter().append('svg:g')
        .attr('transform', (d, i) => 'translate(0,' + i * (li.h + li.s) + ')')
        .style('pointer-events', 'all')
        .style('cursor', 'pointer')
        .on('click', (d) => {
          console.log('pie legend click');
          _this.router.drilldownFilter(d.value);
          _this.$rootScope.$digest();
        })
      ;

      g.append('svg:rect')
        .attr('x', '10px')
        .attr('rx', li.r)
        .attr('ry', li.r)
        .attr('width', li.h)//"20px"//li.w)
        .attr('height', li.h)
        .style('stroke', 'white')
        .style('stroke-width', '1')
        .style('fill', d => d.val)
        .style('opacity', 1)
      ;

      g.append('svg:text')
        .attr('x', li.h + 150)
        .attr('y', li.h / 2)
        .attr('dy', '0.35em')
        .attr('width', li.w - li.h)
        .attr('text-anchor', 'left')
        .text(d => d.key);

      g.append('svg:text')
        .attr('x', li.h + 15)
        .attr('y', li.h / 2)
        .attr('dy', '0.35em')
        .attr('width', li.w - li.h)
        .attr('text-anchor', 'left')
        .text(d => Measure.getMeasure(
          d.value.data,
          _this.aggregate.filter.measure,
        ));
    }

    // Take a 2-column CSV and transform it into a hierarchical structure suitable
    // for a partition layout. The first column is a sequence of step names, from
    // root to leaf, separated by hyphens. The second column is a count of how
    // often that sequence occurred.
    function buildHierarchy(csv) {

      if (!aggregate || !aggregate.filter || !aggregate.filter.drilldown) {
        return null;
      }

      let root = {'name': 'root', 'children': []};

      dims = [];
      for (let i = 0; i < csv.length; i++) {

        let size = csv[i][_this.aggregate.filter.measure];
        let parts = [];

        let drills = aggregate.filter.drilldown;
        for (let j = 0; j < drills.length; j++) {
          dims.push({});
          let filter = _this.model.getCellFilter(drills[j], csv[i]);
          filter.data = size;
          if (dims[j][filter.label])
            dims[j][filter.label].data += size;
          else
            dims[j][filter.label] = filter;

          parts.push(filter);
        }

        let currentNode = root;
        for (let j = 0; j < parts.length; j++) {
          let children = currentNode['children'];
          let filter = parts[j];
          let nodeName = filter.label;
          let childNode;
          if (j + 1 < parts.length) {
            // Not yet at the end of the sequence; move down the tree.
            let foundChild = false;
            for (let k = 0; k < children.length; k++) {
              if (children[k]['name'] === nodeName) {
                childNode = children[k];
                foundChild = true;
                break;
              }
            }
            // If we don't already have a child node for this branch, create it.
            if (!foundChild) {
              childNode = {'name': nodeName, 'filter': filter, 'children': []};
              children.push(childNode);
            }
            currentNode = childNode;
          } else {
            // Reached the end of the sequence; create a leaf node.
            childNode = {'name': nodeName, 'filter': filter, 'size': size};
            children.push(childNode);
          }
        }
      }
      let color = [d3.scale.category10(), () => '#0088cc'];
      for (let di = 0; di < dims.length; di++) {
        let dim = dims[di];
        let index = 0;
        let single = Object.keys(dims[0]).length === 1;
        for (let d in dim) {
          if (( single && di === 0) || (!single && di > 0))
            colors[d] = color[1](index++ % 10);
          else
            colors[d] = color[0](index++ % 10);
        }
      }
      return root;
    }

    let json = buildHierarchy(aggregate.cells);
    if (json) createVisualization(json);
  }
}

export const PieComponent = {
  template  : require('./pie.component.html'),
  bindings  : {
    model      : '<',
    aggregate  : '<',
  },
  controller: PieController,
};



// WEBPACK FOOTER //
// ./src/stat/pie.component.js